import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Download, ArrowLeft, Folder } from 'lucide-react';
import Logo from '../components/Logo';
import axios from 'axios';

const ProDocumentsPage = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('pro_token');
    if (!token) {
      navigate('/pro/login');
      return;
    }
    fetchDocuments();
    fetchCategories();
  }, [navigate]);

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('pro_token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${BACKEND_URL}/api/pro/documents/`, { headers });
      setDocuments(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError(err.response?.data?.detail || 'Erreur lors du chargement des documents');
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('pro_token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${BACKEND_URL}/api/pro/documents/categories`, { headers });
      setCategories(response.data.categories || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const filteredDocuments = selectedCategory === 'all' 
    ? documents 
    : documents.filter(doc => doc.category === selectedCategory);

  const getFileIcon = (fileType) => {
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return '📊';
    if (fileType.includes('word') || fileType.includes('document')) return '📝';
    if (fileType.includes('image')) return '🖼️';
    return '📁';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-celadon mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des documents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Logo showTagline={true} clickable={true} />
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/pro/dashboard')}
          className="flex items-center gap-2 text-celadon hover:text-celadon-light mb-6"
        >
          <ArrowLeft size={20} />
          Retour au Dashboard
        </button>

        {/* Title */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
            <FileText size={32} className="text-celadon" />
            Documents & Catalogues PRO
          </h1>
          <p className="text-gray-600">Accédez à tous vos documents techniques et catalogues exclusifs</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Category Filter */}
        <div className="mb-6 flex items-center gap-4">
          <span className="text-gray-700 font-medium">Catégorie :</span>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                selectedCategory === 'all'
                  ? 'bg-celadon text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
              }`}
            >
              Tous ({documents.length})
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-lg transition-colors capitalize ${
                  selectedCategory === cat
                    ? 'bg-celadon text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                {cat} ({documents.filter(d => d.category === cat).length})
              </button>
            ))}
          </div>
        </div>

        {/* Documents Grid */}
        {filteredDocuments.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <Folder size={64} className="text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-900 mb-2">Aucun document disponible</h3>
            <p className="text-gray-600">
              {selectedCategory === 'all'
                ? 'Aucun document n\'est disponible pour le moment.'
                : `Aucun document dans la catégorie "${selectedCategory}".`}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredDocuments.map((doc) => (
              <div
                key={doc.id}
                className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow p-6 border border-gray-200"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="text-4xl">{getFileIcon(doc.file_type)}</div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-gray-900 mb-1 truncate">{doc.title}</h3>
                    <span className="inline-block px-2 py-1 bg-celadon bg-opacity-10 text-celadon text-xs rounded capitalize">
                      {doc.category}
                    </span>
                  </div>
                </div>

                {doc.description && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{doc.description}</p>
                )}

                <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                  <span>{doc.file_name}</span>
                  <span>{formatFileSize(doc.file_size)}</span>
                </div>

                <a
                  href={`${BACKEND_URL}${doc.file_url}`}
                  download
                  className="flex items-center justify-center gap-2 w-full bg-celadon text-white py-2 rounded hover:bg-celadon-light transition-colors"
                >
                  <Download size={18} />
                  Télécharger
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProDocumentsPage;
